import { useCallback, useEffect, useRef, useState } from 'react';

import braintree from 'braintree-web';

import { logger } from 'utils/logger';

interface BraintreeInstances {
  client?: any;
  dataCollector?: any;
  paypalCheckout?: any;
  venmo?: any;
}

interface UseBraintreeInitializationProps {
  isBraintreePayment: boolean;
  isPayPalPayment: boolean;
  isVenmoPayment: boolean;
  braintreeAuthorizationKey: string;
}

type UseBraintreeInitializationReturnType = {
  braintreeInstances: BraintreeInstances;
  isInitializing: boolean;
};

const createBraintreeClient = async (braintreeAuthorizationKey: string) =>
  await braintree.client.create({
    authorization: braintreeAuthorizationKey,
  });

const createDataCollector = async (client: any) =>
  await braintree.dataCollector.create({
    client,
    paypal: true,
  });

const createPayPalInstance = async (client: any) => await braintree.paypal.create({ client });

const createVenmoInstance = async (client: any) =>
  await braintree.venmo.create({
    client,
    allowDesktop: true,
    mobileWebFallBack: true,
    // @ts-ignore - this is in their docs but not on the type for some reason
    requireManualReturn: true, // require user manually switch back to the website. There is no other way I can find to get this to work otherwise!
  });

/**
 * Initializes Braintree instances for PayPal and Venmo to fix browser popup blocking issues on WEB.
 *
 * Problem: Due to the asynchronous nature of Braintree's create calls, when tokenize is called
 * after a user click, browsers don't recognize it as a direct effect of the user's action. This
 * causes popup blockers to prevent PayPal/Venmo payment flows from working properly.
 *
 * Solution: Pre-initialize Braintree instances during component mount rather than
 * during the payment flow. This ensures that when tokenize() is called directly after a user click,
 * the browser recognizes it as a legitimate user-initiated action and doesn't block the popup.
 *
 */
export const useBraintreeInitialization = ({
  isBraintreePayment,
  isPayPalPayment,
  isVenmoPayment,
  braintreeAuthorizationKey,
}: UseBraintreeInitializationProps): UseBraintreeInitializationReturnType => {
  const [isInitializing, setIsInitializing] = useState(false);

  const braintreeInstancesRef = useRef<BraintreeInstances>({});

  const initializeBraintreeInstances = useCallback(async () => {
    if (isInitializing) {
      return;
    }

    setIsInitializing(true);

    try {
      let client = braintreeInstancesRef.current.client;
      if (!client) {
        client = await createBraintreeClient(braintreeAuthorizationKey);
      }

      let dataCollector = braintreeInstancesRef.current.dataCollector;
      if (!dataCollector) {
        dataCollector = await createDataCollector(client);
      }

      if (isPayPalPayment && !braintreeInstancesRef.current.paypalCheckout) {
        const paypalCheckout = await createPayPalInstance(client);
        braintreeInstancesRef.current = {
          client,
          dataCollector,
          paypalCheckout,
          venmo: undefined,
        };
      } else if (isVenmoPayment && !braintreeInstancesRef.current.venmo) {
        const venmo = await createVenmoInstance(client);
        braintreeInstancesRef.current = {
          client,
          dataCollector,
          venmo,
          paypalCheckout: undefined,
        };
      }
    } catch (error) {
      logger.error({
        message: '[useBraintreeInitialization] Error initializing Braintree instances',
        error,
        isPayPalPayment,
        isVenmoPayment,
      });
      braintreeInstancesRef.current = {};
    } finally {
      setIsInitializing(false);
    }
  }, [isInitializing, isPayPalPayment, isVenmoPayment, braintreeAuthorizationKey]);

  const resetInstances = useCallback(() => {
    braintreeInstancesRef.current = {};
  }, []);

  useEffect(() => {
    if (!isBraintreePayment) {
      resetInstances();
      return;
    }

    const needsInitialization =
      !braintreeInstancesRef.current.client ||
      (isPayPalPayment && !braintreeInstancesRef.current.paypalCheckout) ||
      (isVenmoPayment && !braintreeInstancesRef.current.venmo);

    if (needsInitialization) {
      initializeBraintreeInstances();
    }
  }, [
    isBraintreePayment,
    isPayPalPayment,
    isVenmoPayment,
    initializeBraintreeInstances,
    resetInstances,
  ]);

  return {
    braintreeInstances: braintreeInstancesRef.current,
    isInitializing,
  };
};
